import * as React from "react";

import {
  VolumeHighIcon,
  VolumeLowIcon,
  VolumeMediumIcon,
  VolumeMutedIcon,
} from "../icons";
import { cn } from "../lib/cn";

export type VolumeBarProps = {
  value: number;
  onVolumeChange: (value: number) => void;
  disabled?: boolean;
  color?: "black" | "white";
  iconPosition?: "start" | "end";
  className?: string;
  ariaLabel?: string;
};

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 1);
}

function getVolumeIcon(value: number) {
  if (value === 0) return VolumeMutedIcon;
  if (value < 0.34) return VolumeLowIcon;
  if (value < 0.67) return VolumeMediumIcon;
  return VolumeHighIcon;
}

function getVolumeState(value: number) {
  if (value === 0) return "muted";
  if (value < 0.34) return "low";
  if (value < 0.67) return "medium";
  return "high";
}

export const VolumeBar = React.forwardRef<HTMLInputElement, VolumeBarProps>(
  (
    {
      value,
      onVolumeChange,
      disabled = false,
      color = "black",
      iconPosition = "start",
      className,
      ariaLabel = "Adjust volume",
    },
    ref,
  ) => {
    const clampedValue = clampVolume(value);
    const Icon = getVolumeIcon(clampedValue);
    const volumeState = getVolumeState(clampedValue);
    const colorVars =
      color === "white"
        ? {
            "--progress-bar-bg": "rgba(255, 255, 255, 0.35)",
            "--progress-bar-fg": "hsl(var(--white))",
            "--progress-bar-thumb": "hsl(var(--white))",
          }
        : undefined;
    const icon = (
      <span
        className={cn(
          "flex size-5 shrink-0 items-center justify-center",
          color === "white" ? "text-white/90" : "text-black/90",
          disabled && "opacity-40",
        )}
        data-testid="volume-bar-icon"
        data-volume-state={volumeState}
      >
        <Icon aria-hidden="true" className="size-5" />
      </span>
    );

    return (
      <div className={cn("flex items-center gap-3", className)}>
        {iconPosition === "start" ? icon : null}
        <input
          ref={ref}
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={clampedValue}
          aria-label={ariaLabel}
          aria-disabled={disabled}
          disabled={disabled}
          className={cn(
            "volume-bar-range w-full rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          )}
          style={
            {
              "--range-height": "0.5rem",
              "--range-progress": `${clampedValue * 100}%`,
              ...colorVars,
            } as React.CSSProperties
          }
          onInput={(event) => onVolumeChange(event.currentTarget.valueAsNumber)}
        />
        {iconPosition === "end" ? icon : null}
      </div>
    );
  },
);
VolumeBar.displayName = "VolumeBar";
